import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from '@hooks/useTranslation';

import WorkpayLogoIconSvg from '@assets/logo-icon.svg';
import WorkpayLogoSvg from '@assets/icons/workpayLogo.svg';

// ============================================
// Sidebar Navigation
// Uses global layout classes from _layout.scss
// ============================================

export interface SidebarSubMenuItem {
  id: string;
  label: string;
  href: string;
}

export interface SidebarMenuItem {
  id: string;
  label: string;
  icon: ReactNode;
  href?: string;
  children?: SidebarSubMenuItem[];
}

export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  menuItems: SidebarMenuItem[];
  activeItemId: string;
  /** Items pinned to the bottom of the sidebar (above the fold) */
  bottomItems?: SidebarMenuItem[];
}

// Chevron SVG for collapse/expand toggle
function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {direction === 'left' ? (
        <path
          d="M11.25 13.5L6.75 9L11.25 4.5"
          stroke="#536171"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M6.75 4.5L11.25 9L6.75 13.5"
          stroke="#536171"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

// Workpay logo — uses full wordmark when expanded, icon-only when collapsed
function WorkpayLogo({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="sidebar__logo">
      <img
        src={collapsed ? WorkpayLogoIconSvg : WorkpayLogoSvg}
        alt={t('K_APP_NAME', 'WorkPay')}
        height={collapsed ? 20 : 24}
        style={{ objectFit: 'contain' }}
      />
    </div>
  );
}

// Small chevron for submenu expand/collapse
function MenuChevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Sidebar({
  collapsed,
  onToggle,
  menuItems,
  activeItemId,
  bottomItems,
}: SidebarProps) {
  const { t } = useTranslation();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Auto-open the parent menu that contains the active sub-item
  const resolvedOpenId = (() => {
    if (openMenuId !== null) return openMenuId;
    for (const item of menuItems) {
      if (item.children?.some((child) => child.id === activeItemId)) {
        return item.id;
      }
    }
    return null;
  })();

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
      <div className="sidebar__header">
        <WorkpayLogo collapsed={collapsed} />

        {/* Collapse/expand toggle — positioned inside header so it sits at the top-right edge */}
        <button
          className="sidebar__toggle"
          onClick={onToggle}
          aria-label={collapsed ? t('K_ARIA_EXPAND_SIDEBAR', 'Expand sidebar') : t('K_ARIA_COLLAPSE_SIDEBAR', 'Collapse sidebar')}
          type="button"
        >
          <ChevronIcon direction={collapsed ? 'right' : 'left'} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav">
        {menuItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isOpen = resolvedOpenId === item.id;
          const isActive = item.id === activeItemId;
          const hasActiveChild = item.children?.some((child) => child.id === activeItemId);

          // Menu item with children (expandable)
          if (hasChildren) {
            return (
              <div
                key={item.id}
                className={`sidebar-menu${isOpen ? ' sidebar-menu--open' : ''}${hasActiveChild ? ' sidebar-menu--active' : ''}`}
              >
                <button
                  className="sidebar-menu__header"
                  onClick={() => setOpenMenuId(isOpen ? null : item.id)}
                  type="button"
                  title={collapsed ? item.label : undefined}
                >
                  <span className="sidebar-menu__icon">{item.icon}</span>
                  <span className="sidebar-menu__label">{item.label}</span>
                  <span className="sidebar-menu__chevron">
                    <MenuChevron />
                  </span>
                </button>

                {/* Sub-menu items */}
                {isOpen && !collapsed && item.children && (
                  <div className="sidebar-submenu">
                    {item.children.map((child) => (
                      <Link
                        key={child.id}
                        to={child.href}
                        className={`sidebar-submenu__item${child.id === activeItemId ? ' sidebar-submenu__item--active' : ''}`}
                        title={child.label}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Simple menu item (no children)
          return (
            <Link
              key={item.id}
              to={item.href!}
              className={`sidebar-menu${isActive ? ' sidebar-menu--selected' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <span className="sidebar-menu__icon">{item.icon}</span>
              <span className="sidebar-menu__label">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      {bottomItems && bottomItems.length > 0 && (
        <div className="sidebar__bottom">
          {bottomItems.map((item) => (
            <Link
              key={item.id}
              to={item.href!}
              className={`sidebar-menu${item.id === activeItemId ? ' sidebar-menu--selected' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <span className="sidebar-menu__icon">{item.icon}</span>
              <span className="sidebar-menu__label">{item.label}</span>
            </Link>
          ))}
        </div>
      )}
    </aside>
  );
}
